import { formatRelativeDay } from '@takumi/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, CheckCheck } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '@/components/ui/button';
import { EmptyState, Popover, PopoverContent, PopoverTrigger } from '@/components/ui/primitives';
import { api } from '@/lib/api';
import type { Notification } from '@/lib/types';
import { cn } from '@/lib/utils';

/** Desktop (browser / Tauri) notification for items that arrive while the app is open. */
function useDesktopNotifications(items: Notification[] | undefined) {
  const seen = useRef<Set<string> | null>(null);
  useEffect(() => {
    if (!items) return;
    if (seen.current === null) {
      seen.current = new Set(items.map((n) => n.id)); // don't replay history on first load
      return;
    }
    const fresh = items.filter((n) => !n.readAt && !seen.current!.has(n.id));
    fresh.forEach((n) => seen.current!.add(n.id));
    if (fresh.length && 'Notification' in window && window.Notification.permission === 'granted' && document.visibilityState !== 'visible') {
      for (const n of fresh.slice(0, 3)) new window.Notification(n.title, { body: n.body ?? undefined, tag: n.id, lang: 'he', dir: 'rtl' });
    }
  }, [items]);
}

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api.get<{ items: Notification[]; unread: number }>('/notifications'),
    refetchInterval: 30_000,
  });
  useDesktopNotifications(data?.items);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['notifications'] });
    void qc.invalidateQueries({ queryKey: ['counts'] });
  };
  const readOne = useMutation({ mutationFn: (id: string) => api.post(`/notifications/${id}/read`), onSuccess: invalidate });
  const readAll = useMutation({ mutationFn: () => api.post('/notifications/read-all'), onSuccess: invalidate });

  const unread = data?.unread ?? 0;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="relative grid size-9 place-items-center rounded-control text-ink-soft hover:bg-sunken" aria-label={`התראות${unread ? ` (${unread} חדשות)` : ''}`}>
          <Bell className="size-[19px]" />
          {unread > 0 && (
            <span className="absolute -top-0.5 -end-0.5 min-w-4.5 rounded-full bg-overdue px-1 text-center text-[10px] leading-4.5 font-bold text-white tabular">
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[22rem] p-0">
        <div className="flex items-center justify-between border-b border-line px-4 py-2.5">
          <span className="font-semibold">התראות</span>
          <div className="flex gap-1">
            {'Notification' in window && window.Notification.permission === 'default' && (
              <Button variant="ghost" size="sm" onClick={() => void window.Notification.requestPermission()}>
                הפעלת התראות שולחן עבודה
              </Button>
            )}
            {unread > 0 && (
              <Button variant="ghost" size="sm" onClick={() => readAll.mutate()}>
                <CheckCheck /> סמן הכל כנקרא
              </Button>
            )}
          </div>
        </div>
        <div className="max-h-[26rem] overflow-y-auto">
          {!data?.items.length ? (
            <EmptyState icon={<Bell />} title="אין התראות" />
          ) : (
            data.items.map((n) => (
              <button
                key={n.id}
                type="button"
                onClick={() => {
                  if (!n.readAt) readOne.mutate(n.id);
                  setOpen(false);
                  if (n.link) navigate(n.link);
                }}
                className={cn('flex w-full gap-3 border-b border-line/70 px-4 py-3 text-start hover:bg-sunken/70', !n.readAt && 'bg-matcha-50/70')}
              >
                <span className={cn('mt-1.5 size-2 shrink-0 rounded-full', n.readAt ? 'bg-transparent' : 'bg-matcha-600')} />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold">{n.title}</span>
                  {n.body && <span className="block truncate text-xs text-muted">{n.body}</span>}
                  <span className="mt-0.5 block text-[11px] text-faint">{formatRelativeDay(n.createdAt)}</span>
                </span>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
